import React, { useEffect, useState } from "react";
import HeaderWithIcon from "../../components/common/HeaderWithIcon";
import ViewDetailsCard from "./ViewDetailsCard";
import dice from "../../assets/icons/dices.svg?react";
import { useParams } from "react-router-dom";
import { gamesApi } from "../../api/games";
import { useAuth } from "../../context/AuthContext";
import dayjs from "dayjs";

export default function ViewDetails() {
  const { tokens } = useAuth();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [gameResults, setGameResults] = useState([]);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("All");

  useEffect(() => {
    if (tokens?.access?.token && id) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [tokens?.access?.token, id]);
  
  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await gamesApi.fetchGameResultsById(tokens.access.token, id);
      
      if (response.status === 200 && response.data && response.data.rows) {
        console.log("API data successfully fetched:", response.data);
        
        // Transform API data
        const transformedData = transformApiData(response.data);
        setGameResults(transformedData);
      } else {
        // Fallback to sample data if API returns empty results
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Failed to fetch data");
      // Fallback to sample data if API fails
    } finally {
      setLoading(false);
    }
  };
  
  // Transform API data to the format we need
  const transformApiData = (apiData) => {
    if (!apiData.rows || !Array.isArray(apiData.rows) || apiData.rows.length === 0) {
      return [];
    }
    
    // Group data by role
    const groupedByRole = {
      All: [],
      Players: [],
      Agents: [],
      Coordinators: []
    };
    
    apiData.rows.forEach(item => {
      const roleName = getRoleName(item.user?.roleId);
      const isFloatingWin = item.user?.id !== item.bets?.User?.id;
      let floatingWinSource = undefined;
      let floatingWinSourceRole = undefined;
      if (isFloatingWin) {
        floatingWinSource = item.bets?.User?.name;
        floatingWinSourceRole = getRoleName(item.bets?.User?.roleId);
      }
      const gameCard = {
        game: item.user?.name || "Unknown",
        role: roleName,
        numbers: formatNumbers(item.bets?.combination),
        drawDate: item.bets?.createdAt ? dayjs(item.bets.createdAt).format("ddd, MMM D, YYYY") : "N/A",
        drawTime: item.bets?.drawTime || "N/A",
        amount: parseFloat(item.bets?.amount) || 0,
        totalHits: item.bets?.bettingOption || "N/A",
        totalWin: item.win || 0,
        ticketNumber: item.bets?.transactionReceiptsId || "N/A",
        isFloatingWin,
        floatingWinSource,
        floatingWinSourceRole,
      };
      
      // Add to All group
      groupedByRole.All.push(gameCard);
      
      // Add to specific role group
      if (roleName === "Coordinator") {
        groupedByRole.Coordinators.push(gameCard);
      } else if (roleName === "Agent") {
        groupedByRole.Agents.push(gameCard);
      } else if (roleName === "Player") {
        groupedByRole.Players.push(gameCard);
      }
    });
    
    // Convert grouped data to the format needed for tabs
    return Object.entries(groupedByRole)
      .filter(([_, cards]) => cards.length > 0)
      .map(([title, cards]) => ({
        title,
        cards
      }));
  };
  
  // Function to get role name from role ID
  const getRoleName = (roleId) => {
    const roleMap = {
      1: "Admin",
      2: "Incorporator",
      3: "Sub Operator",
      4: "General Coordinator",
      5: "Coordinator",
      6: "Sub Coordinator",
      7: "Agent",
      8: "Sub Agent",
      9: "Player"
    };
    return roleMap[roleId] || "Player";
  };
  
  // Format numbers for display
  const formatNumbers = (combination) => {
    if (!combination) return [];
    
    // Simply convert each digit to a number object
    return combination.split('').map(digit => ({
      num: parseInt(digit)
    }));
  };
  
  // Set sample data as fallback
  const setSampleData = () => {
    const simpleData = [
      {
        title: "All",
        cards: [
          {
            game: "Sheen Sheen",
            role: "Agent",
            numbers: [
              { num: 6 },
              { num: 0 },
              { num: 5 }
            ],
            drawDate: "Wed, Aug 28, 2024",
            drawTime: "2:00 PM",
            amount: 5.00,
            totalHits: "Ramble",
            totalWin: 583.33,
            ticketNumber: "16f5008f-03d9-4a35-a56d-75afcc33426b"
          }
        ]
      },
      {
        title: "Players",
        cards: [
          {
            game: "Ronnie Acosta",
            role: "Player",
            numbers: [
              { num: 6 },
              { num: 0 },
              { num: 5 }
            ],
            drawDate: "Wed, Aug 28, 2024",
            drawTime: "2:00 PM",
            amount: 10.00,
            totalHits: "Ramble",
            totalWin: 1166.00,
            ticketNumber: "a56f13e3-e8c6-45c8-864b-f0f845de09c1"
          }
        ]
      },
      {
        title: "Agents",
        cards: [
          {
            game: "Sheen Sheen",
            role: "Agent",
            numbers: [
              { num: 6 },
              { num: 0 },
              { num: 5 }
            ],
            drawDate: "Wed, Aug 28, 2024",
            drawTime: "2:00 PM",
            amount: 5.00,
            totalHits: "Ramble",
            totalWin: 583.33,
            ticketNumber: "16f5008f-03d9-4a35-a56d-75afcc33426b"
          }
        ]
      }
    ];
    console.log("Setting fallback sample data:", simpleData);
    setGameResults(simpleData);
  };

  // Get the active tab data
  const getActiveTabData = () => {
    const activeTabData = gameResults.find(tab => tab.title === activeTab);
    return activeTabData?.cards || [];
  };

  return (
    <div className="min-h-screen relative overflow-hidden font-poppins">
      <HeaderWithIcon
        title="View Details"
        icon={dice}
        className="bg-[#0E64D1] text-white"
      />

      <div className="mt-4 px-4">
        {loading ? (
          <div className="flex justify-center items-center h-[calc(100vh-100px)]">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0E64D1]"></div>
          </div>
        ) : error ? (
          <div className="flex justify-center items-center h-[calc(100vh-100px)] text-red-500">
            {error}
          </div>
        ) : gameResults.length === 0 ? (
          <div className="flex justify-center items-center h-[calc(100vh-100px)] text-gray-500">
            No results found
          </div>
        ) : (
          <div>
            {/* Custom Tab Implementation */}
            <div>
              <div className="flex justify-center gap-1.5">
                {gameResults.map((tab, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveTab(tab.title)}
                    className={`py-2.5 px-3 rounded-md text-xs font-normal transition-all duration-300 ${
                      activeTab === tab.title
                        ? "bg-[#65758B] text-white"
                        : "bg-[#E1E7EF] text-[#65758B] hover:text-white"
                    }`}
                  >
                    {tab.title}
                  </button>
                ))}
              </div>

              {/* Display Cards for Active Tab */}
              <div className="mt-6">
                {getActiveTabData().length > 0 ? (
                  getActiveTabData().map((card, idx) => (
                    <div key={idx} className="mb-4">
                      <ViewDetailsCard {...card} />
                    </div>
                  ))
                ) : (
                  <div className="text-center text-gray-500 py-4">
                    No cards available in this tab
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
